import { Controller } from "@hotwired/stimulus"
import { SlickGrid } from "slickgrid"
import Plotly from "plotly.js-dist"

export default class extends Controller {
    static get targets() {
        const categories = ["enhanced", "conservative", "tier2", "us", "liquidity"];
        const tabSuffixes = ["mainTab", "holdingsTab", "transactionHistoryTab", "characteristicsTab", "breakdownTab"];
        const mainChartSuffixes = ["performanceTable", "performanceChart", "securitiesBreakdownChart", "issuersChart", "creditQualityChart", "expectedMaturityChart", "sectorsChart", "capitalStructureChart", "paymentStructureChart", "capitalPriceDistributionChart"];
        const characteristicsChartSuffixes = ["yieldToExpectedCallChart", "cashRunningYieldChart", "creditRatingBreakdownChart", "capitalStructureBreakdownChart", "creditSpreadAndCreditDurationChart", "interestDurationChart", "termToCallChart", "cleanPriceChart"];
        const breakdownChartSuffixes = ["creditRatingBreakdownChart", "capitalStructureBreakdownChart", "paymentBreakdownChart"];
        const holdingsChartSuffixes = ["holdingsTable"];
        const transactionHistoryChartSuffixes = ["transactionHistoryTable"];

        const targets = [];
        categories.forEach(category => {
            tabSuffixes.forEach(suffix => targets.push(`${category}_${suffix}`));
            mainChartSuffixes.forEach(suffix => targets.push(`${category}_${suffix}`));
            characteristicsChartSuffixes.forEach(suffix => targets.push(`${category}_${suffix}`));
            breakdownChartSuffixes.forEach(suffix => targets.push(`${category}_${suffix}`));
            holdingsChartSuffixes.forEach(suffix => targets.push(`${category}_${suffix}`));
            transactionHistoryChartSuffixes.forEach(suffix => targets.push(`${category}_${suffix}`));
        });
        return targets;
    }

    connect(){
        this.chartBackgroundColor = '#e5ecf6'
        this.chartGridColor = '#ffffff'
        this.extendedColorPalette = [
            '#DC6122', '#7E2728', '#E9BDA9', '#A8593A', '#611C1E',
            '#90A29C', '#F4D6B3', '#8C492C', '#E4CFC9',
            '#A6594C', '#A63421', '#993E3F', '#C0463E',

            '#F0DDD8', '#A64D4E', '#F2CFC2', '#C87D5A', '#8D4A4B',
            '#B5C4BE', '#F7E5CC', '#B56B4C', '#E87E42',
            '#C77C6C', '#C55441', '#B75F5F', '#D66E5E',

            '#D1B5AF', '#5D1E1F', '#D4A589', '#7A462A', '#4A1317',
            '#6B7B75', '#E0C193', '#663622', '#A84A1A',
            '#8A453C', '#7A2A19', '#7A2F2F', '#9A3A3A',

            '#B8A5A0', '#9C3E3F', '#C4957A', '#6F4A35', '#5A2025',
            '#7A8B85', '#D4BE9E', '#7A4A32', '#B85A25',
            '#9A5A4C', '#8A3A29', '#8A3E3E', '#AA4E4E'
        ]
        this.mainId = "enhanced"
        this.subId = "main"
        this.performanceGrid = false
        this.chartDataCache={}
        this.holdingsGrid = false
        this.transactionHistoryGrid = false
        this.setupUiKitTabsStyle()
        this.boundHandleTabChange = this.handleTabChange.bind(this)
        this.element.addEventListener('uikit--tab:tabChanged', this.boundHandleTabChange)
        this.loadMainSubTab(this.mainId)
    }

    setupUiKitTabsStyle(){
        const style = document.createElement('style')
        style.id = 'model-portfolios-styles'
        style.textContent = `
            .model-portfolios ul {
                display: flex !important;
                align-items: center !important;
                width: 100% !important;
                justify-content: space-evenly !important;
            }
            .model-portfolios li {
                width: calc(100% / 5);
                margin-inline-end:0 !important;
            }
            .modebar-group {
                display: flex !important;
                flex-direction: row !important;
                flex-wrap: nowrap !important;
                align-items: center !important;
            }
        `
        document.head.appendChild(style)
    }

    disconnect(){
        this.element.removeEventListener('uikit--tab:tabChanged', this.boundHandleTabChange)
        const chartTargets = this.element.querySelectorAll('[id*="Chart"]')
        chartTargets.forEach(target => {
            if (target._resizeCleanup) {
                target._resizeCleanup()
            }
        })
    }

    getChartParams(mainId,key){
        return {
            main_id: mainId,
            key: key
        }
    }
    getChartLayout(xTitle = "",yTitle = "",title = ""){
        return {
            plot_bgcolor: this.chartBackgroundColor,
            xaxis: {
                title: xTitle,
                showgrid: true,
                gridcolor: this.chartGridColor,
                gridwidth: 1,
                type: 'date',
                zeroline: false
            },
            yaxis: {
                title: yTitle,
                showgrid: true,
                gridcolor: this.chartGridColor,
                gridwidth: 1,
                tickformat: '.3s',
                zeroline: false
            },
            legend: {
                x: 0,
                y: 1,
                bgcolor: 'rgba(255,255,255,0.8)'
            },
            hovermode: 'closest'
        }
    }
    getPieChartLayout(){
        return {
            showlegend: true,
            legend: {
                orientation: "v",
                x: 1.15,
                y: 0.5,
                xanchor: 'left',
                yanchor: 'middle',
                bgcolor: 'rgba(255,255,255,0.8)',
                width: 20
            },
            margin: {
                r: 150
            }
        }
    }
    getBarChartLayout(xTitle = "",yTitle = "",title = ""){
        const layout = {
            plot_bgcolor: this.chartBackgroundColor,
            xaxis: {
                title: xTitle,
                showgrid: false,
                showline: false,
                zeroline: false
            },
            yaxis: {
                title: yTitle,
                gridcolor: this.chartGridColor,
                gridwidth: 1,
                showgrid: true,
                showline: false,
                zeroline: false
            },
            hovermode: 'closest',
            showlegend: false,

        }

        return layout
    }

    getMultiAreaChartLayout(xTitle = "", yTitle = "") {
        return {
            xanchor: 'center',
            yanchor: 'bottom',
            plot_bgcolor: this.chartBackgroundColor,
            xaxis: {
                title: xTitle,
                showgrid: true,
                gridcolor: this.chartGridColor,
                type: 'date',
                zeroline: false
            },
            yaxis: {
                title: yTitle,
                showgrid: true,
                gridcolor: this.chartGridColor,
                tickformat: '.1f',
                zeroline: false
            },
            legend: {
                orientation: "v",
                x: 1.02,
                y: 0.5,
                bgcolor: 'rgba(255,255,255,0.8)'
            },
            hovermode: 'closest'
        }
    }

    hexToRgba(hex, opacity = 1) {
        hex = hex.replace('#', '')
        const r = parseInt(hex.substring(0, 2), 16)
        const g = parseInt(hex.substring(2, 4), 16)
        const b = parseInt(hex.substring(4, 6), 16)
        return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }

    convertDataForSlickGrid(data) {
        if (!data || typeof data !== 'object') return []
        const columns = Object.keys(data)
        if (columns.length === 0) return []
        const firstColumn = data[columns[0]]
        if (!firstColumn || typeof firstColumn !== 'object') return []
        
        const rowIndices = Object.keys(firstColumn)
        const results = []
        rowIndices.forEach(rowIndex => {
            const row = {}
            columns.forEach(columnName => {
                const columnData = data[columnName]
                let value = columnData && columnData[rowIndex] !== undefined 
                    ? columnData[rowIndex] 
                    : null
                row[columnName] = this.formatValue(value)
            })
            results.push(row)
        })
        return results
    }

    formatValue(value) {
        if (value === null || value === undefined) {
            return ''
        }
        if (typeof value === 'string') {
            if (this.isDateString(value)) {
                return this.formatDate(value)
            }
            return value
        }
        if (typeof value === 'number') {
            if (Number.isInteger(value)) {
                return this.formatNumber(value)
            }
            if (value % 1 !== 0) {
                return this.formatCurrency(value)
            }
            return value.toString()
        }
        return value.toString()
    }

    convertPerformanceDataForSlickGrid(data) {
        if (!data || typeof data !== 'object') return []
        const metricsData = data['Performance Metrics']
        if (!metricsData) return []
        
        const metrics = Object.values(metricsData) // ['1m', '3m', '6m', '1y', '3y p.a.', 'Inception (cum)']
        const results = []
        Object.keys(data).forEach(fundName => {
            if (fundName === 'Index' || fundName === 'Performance Metrics') return
            const fundData = data[fundName]
            if (!fundData) return
            const row = { Fund: fundName }
            metrics.forEach((metric, index) => {
                const value = fundData[index.toString()]
                row[metric] = this.formatPerformanceValue(value)
            })
            results.push(row)
        })
        return results
    }

    formatPerformanceValue(value) {
        if (value === null || value === undefined) {
            return ''
        }
        
        if (typeof value === 'number') {
            return `${(value * 100).toFixed(2)}%`
        }
        return value.toString()
    }

    isDateString(str) {
        if (typeof str !== 'string') return false
        const hasDatePattern = /\d{4}-\d{2}-\d{2}|\d{2}\/\d{2}\/\d{4}|\d{4}\/\d{2}\/\d{2}/.test(str)
        return hasDatePattern && !isNaN(Date.parse(str))
    }

    formatDate(dateString) {
        if (!dateString) return ''
        const date = new Date(dateString)
        return date.toLocaleDateString('en-AU', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit' 
        })
    }

    formatCurrency(value) {
        if (value === null || value === undefined) return ''
        return parseFloat(value)
    }

    formatNumber(value) {
        if (value === null || value === undefined) return ''
        return parseInt(value).toLocaleString()
    }
    drawChart(chartTarget, traces, layout){
        const hasData = traces && traces.length
        const finalLayout = {
            margin: {
                t: 20,
                r: 20,
                b: 50,
                l: 50
            },
            ...layout,
            annotations: hasData ? []: [{ 
                x: 0.5, y: 0.5, xref: 'paper', yref: 'paper', 
                text: 'No data available', 
                showarrow: false, font: { size: 16, color: 'gray' },
            }],
            modebar: {
                orientation: 'h',
                x: 1,
                y: 1,
                xanchor: 'right',
                yanchor: 'top',
                bgcolor: 'rgba(255,255,255,0.8)',
                color: '#000',
                activecolor: '#000'
            },
            responsive: true,
            autosizable: true
        }
        const config = {
            responsive: true,
            autosizable: true
        }
        Plotly.newPlot(chartTarget, traces, finalLayout, config)
        
        const resizeObserver = new ResizeObserver(() => {
            if (chartTarget && chartTarget.offsetParent !== null) {
                try {
                    Plotly.Plots.resize(chartTarget)
                } catch (error) {
                    console.warn('Plotly resize failed:', error)
                }
            }
        })
        resizeObserver.observe(chartTarget)

        const handleResize = () => {
            if (chartTarget && chartTarget.offsetParent !== null) {
                try {
                    Plotly.Plots.resize(chartTarget)
                } catch (error) {
                    console.warn('Plotly resize failed:', error)
                }
            }
        }
        window.addEventListener('resize', handleResize)
        
        chartTarget._resizeCleanup = () => {
            resizeObserver.disconnect()
            window.removeEventListener('resize', handleResize)
        }  
    }

    showLoading(target) {
        const card = document.querySelector(`[data-loading-target="${target}"]`)
        const spinner = card?.querySelector('.loading-spinner')
        if (spinner) spinner.classList.remove('hidden')
    }

    hideLoading(target) {
        const card = document.querySelector(`[data-loading-target="${target}"]`)
        const spinner = card?.querySelector('.loading-spinner')
        if (spinner) spinner.classList.add('hidden')
    }

    async getChartDataByFilterData(mainId,key){
        const params = this.getChartParams(mainId,key)
        const cacheKey = `model_portfolios-${JSON.stringify(params)}`
        try {
            if (this.chartDataCache[cacheKey]) {
                return this.chartDataCache[cacheKey]
            }
            const queryString = new URLSearchParams(params).toString()

            const response = await fetch(`/quant/model_portfolio/model_portfolios/?${queryString}`)
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
            
            const data = await response.json()
            this.chartDataCache[cacheKey] = data
            return data
          } catch (error) {
            return []
          }
    }

    async handleTabChange(event) {
        const tabId = event.detail.tabId
        const ids = tabId.split('-')
        const newMainId = ids[0] || "enhanced"
        
        if (newMainId !== this.mainId) {
            this.mainId = newMainId
        this.subId = "main"
            this.activateMainTabUI(newMainId)
            return 
        } else {
            this.mainId = newMainId
            this.subId = "main"
            if (tabId.includes("-")) { 
                this.subId = ids[1] || "main" 
            }
        }
        switch(this.subId) {
            case "main":
                await this.loadMainSubTab(this.mainId)
                break
            case "characteristics":
                this.loadCharacteristicsTab(this.mainId)
                break
            case "breakdown":
                this.loadBreakdownTab(this.mainId)
                break
            case "holdings":
                this.loadHoldingsTab(this.mainId)
                break
            case "transactions":
                this.loadTransactionsTab(this.mainId)
                break
            default:
                break
        }
    }

    activateMainTabUI(mainId) {
        const mainTabId = `${mainId}-main`
        
        const tabEvent = new CustomEvent('uikit--tab:tabChanged', {
            detail: { tabId: mainTabId },
            bubbles: true
        })
        setTimeout(() => {
            this.element.dispatchEvent(tabEvent)
        }, 50)
    }

    loadMainSubTab(mainId){       
        this.updateTable("performance_table.csv", mainId, "performanceTable", this.performanceGrid, this.getPerformanceColumns())

        this.updateCharts("portfolio_performance.csv", mainId, "performanceChart", "Portfolio Performance", "Date", "Value", "line", "Date", "Value")
        this.updateCharts("securities.csv", mainId, "securitiesBreakdownChart", "Securities Breakdown", "Code", "Portfolio Weight", "pie", "Code", "Portfolio Weight")
        this.updateCharts("issuer_types.csv", mainId, "issuersChart", "Issuers", "Code", "Percentage", "pie", "Code", "Percentage")
        this.updateCharts("ratings_credit_ratings.csv", mainId, "creditQualityChart", "Credit Rating", "Credit Rating", "Credit Rating Percentage", "bar", "Credit Rating", "Credit Rating Percentage")
        this.updateCharts("term_types.csv", mainId, "expectedMaturityChart", "Expected Maturity Breakdown", "Term", "Percentage", "bar", "Term", "Percentage")
        this.updateCharts("sector.csv", mainId, "sectorsChart", "Sectors Breakdown", "Sector", "Portfolio Weight", "time_series_pie", "Sector", "Portfolio Weight")
        this.updateCharts("capital_structure_types.csv", mainId, "capitalStructureChart", "Capital Structure Breakdown", "Type", "Type Percentage", "pie", "Type", "Type Percentage")
        this.updateCharts("payments_types.csv", mainId, "paymentStructureChart", "Payment Structure", "Type","Percentage", "pie", "Type", "Percentage")
        this.updateCharts("prices.csv", mainId, "capitalPriceDistributionChart", "Capital Price Distribution", "Price", "Percentage", "bar", "Price", "Percentage")
    }

    async loadCharacteristicsTab(mainId){
        this.updateCharts("finances.csv", mainId, "yieldToExpectedCallChart", "Yield to Expected Call", "Date", "Yield", "line_scatter", "Date", "Yield")
        this.updateCharts("finances.csv", mainId, "cashRunningYieldChart", "Cash Running Yield", "Date", "Running Yield", "line_scatter", "Date", "Running Yield")
        this.updateCharts("finances.csv", mainId, "creditSpreadAndCreditDurationChart", "Credit Spread and Credit Duration", "Date", "Credit Value", "multi_line_scatter", "Date", "Credit Value")
        this.updateCharts("finances.csv", mainId, "interestDurationChart", "Interest Duration", "Date", "Modified Duration", "line_scatter", "Date", "Modified Duration")
        this.updateCharts("finances.csv", mainId, "termToCallChart", "Term to Call", "Date", "Tenor", "line_scatter", "Date", "Tenor")
        this.updateCharts("finances.csv", mainId, "cleanPriceChart", "Clean Price", "Date", "Capital Price", "line_scatter", "Date", "Capital Price")
    }

    async loadBreakdownTab(mainId){
        this.updateCharts("rating_breakdown.csv", mainId, "creditRatingBreakdownChart", "Credit Rating Breakdown", "Date", "Percentage", "multi_area", "Date", "Percentage")
        this.updateCharts("structure.csv", mainId, "capitalStructureBreakdownChart", "Capital Structure Breakdown", "Date", "Type Percentage", "multi_area", "Date", "Type Percentage")
        this.updateCharts("type_breakdown.csv", mainId, "paymentBreakdownChart", "Payment Breakdown", "Date", "Percentage", "multi_area", "Date", "Percentage")
    }

    async loadHoldingsTab(mainId) {
        this.updateTable("holdings.csv", mainId, "holdingsTable", this.holdingsGrid, this.getHoldingsColumns())
    }

    async loadTransactionsTab(mainId) {
        this.updateTable("transactions.csv", mainId, "transactionHistoryTable", this.transactionHistoryGrid, this.getTransactionHistoryColumns())
    }

    generateLineChartTraces(data){
        const traces = []
        const dates = Object.values(data.Date)
        let colorIndex = 0
        Object.keys(data).forEach(seriesName => {
            if (seriesName !== 'Date') {
                const values = Object.values(data[seriesName]) || 0
                traces.push({
                    x: dates,
                    y: values,
                    type: 'scatter',
                    mode: 'lines',
                    name: seriesName,
                    line: {
                        width: 2,
                        color: this.extendedColorPalette[colorIndex % this.extendedColorPalette.length]
                    },
                    hovertemplate: `Index=${seriesName}<br>` +
                                  `Date=%{x}<br>` +
                                  `Value=%{y:.6f}<br>` +
                                  `<extra></extra>`,
                    hoveron: 'points'
                })
                colorIndex++
            }
        })
        return traces
    }

    generatePieChartTraces(data,xTarget,yTarget) {
        const labels = Object.values(data[xTarget])
        const values = Object.values(data[yTarget])
        
        const processedValues = values.map(val => {
            if (val < 1) {
                return val * 100 
            }
            if (val === null) return 0
            return val
        })
        
        let finalLabels = [...labels]
        let finalValues = [...processedValues]
        
        const combined = labels.map((label, index) => ({
            label: label,
            value: processedValues[index]
        }))
        
        const nonZeroItems = combined.filter(item => item.value > 0)
        const zeroItems = combined.filter(item => item.value === 0)
        
        if (zeroItems.length > 6) {
            finalLabels = nonZeroItems.map(item => item.label).concat(['Other'])
            finalValues = nonZeroItems.map(item => item.value).concat([0])
        }
        
        const trace = {
            labels: finalLabels,
            values: finalValues,
            type: 'pie',
            textinfo: 'percent',
            textposition: 'auto', 
            automargin: true,
            marker: {
                colors: this.extendedColorPalette
            },
            hovertemplate: xTarget + ': %{label}<br>' +
                            yTarget + ': %{value}%<br>' +
                        '<extra></extra>',
            text: finalLabels.map((label, index) => 
                label === 'Other' ? '0%' : ''
            ),
            texttemplate: finalLabels.map((label, index) => 
                label === 'Other' ? '0%' : '%{percent}'
            )
        }
        
        return [trace]
    }

    generateBarChartTraces(data, xTarget, yTarget) {
        const labels = Object.values(data[xTarget])
        const values = Object.values(data[yTarget])
        
        const processedValues = values.map(val => {
            if (val < 1) {
                return parseFloat((val)) 
            }
            if (val === null) return 0
            return parseFloat(val)
        })
        
        const trace = {
            x: labels,
            y: processedValues,
            type: 'bar',
            marker: {
                color: '#e46630'
            },
            hovertemplate: '<span style="color: white;">' + xTarget + ': %{x}<br>' +
                            yTarget + ': %{y}<br>' +
                            '</span><extra></extra>'
        }
        return [trace]
    }

    generateLineScatterChartTraces(data, xTarget, yTarget) {
        const labels = Object.values(data[xTarget])
        const values = Object.values(data[yTarget])
        
        const processedValues = values.map(val => {
            if (val === null) return 0
            return val
        })
        const trace = {
            x: labels,
            y: processedValues,
            type: 'scatter',
            mode: 'lines+markers',
            marker: {
                color: "#dc6122",
                size: 8
            },
            line: {
                width: 2,
                color: "#dc6122"
            },
            hovertemplate: xTarget + ': %{x}<br>' +
                            yTarget + ': %{y}%<br>' +
                          '<extra></extra>'
        }
        return [trace]
    }

    meltData(data, idVar, valueVars, varName, valueName) {
        const melted = {
            [idVar]: [],
            [varName]: [],
            [valueName]: []
        }
        const dates = Object.values(data[idVar])
        valueVars.forEach(varCol => {
            if (data[varCol]) {
                const values = Object.values(data[varCol])
                dates.forEach((date, index) => {
                    melted[idVar].push(date)
                    melted[varName].push(varCol)
                    melted[valueName].push(values[index] || 0)
                })
            }
        })
        return melted
    }

    generateMultiLineScatterChartTraces(meltedData, xTarget, yTarget, groupTarget) {
        const traces = []
        const dates = meltedData[xTarget]
        const values = meltedData[yTarget]
        const groups = meltedData[groupTarget]
        
        const uniqueGroups = [...new Set(groups)]
        const colors = this.extendedColorPalette
        
        uniqueGroups.forEach((group, groupIndex) => {
            const groupDates = []
            const groupValues = []
            
            groups.forEach((g, index) => {
                if (g === group) {
                    groupDates.push(dates[index])
                    groupValues.push(values[index] || 0)
                }
            })
            
            traces.push({
                x: groupDates,
                y: groupValues,
                type: 'scatter',
                mode: 'lines+markers',
                name: group,
                marker: {
                    color: colors[groupIndex % colors.length],
                    size: 8
                },
                line: {
                    width: 2,
                    color: colors[groupIndex % colors.length]
                },
                hovertemplate: '<b>%{x}</b><br>' +
                              group + ': %{y}<br>' +
                              '<extra></extra>'
            })
        })
        return traces
    }

    generateMultiAreaChartTraces(data, options = {}) {
        const {
            dateColumn = 'Date',
            excludeColumns = [],
            colorPalette = this.extendedColorPalette,
            convertToPercentage = true,
            fillType = 'tonexty', // 'tonexty', 'tozeroy', 'tonext'
            stackGroup = 'one',
            lineWidth = 2,
            showMarkers = false,
            fillOpacity = 0.5
        } = options
        const traces = []
        const dates = Object.values(data[dateColumn])
        let colorIndex = 0
        Object.keys(data).forEach(seriesName => {
            if (seriesName !== dateColumn && !excludeColumns.includes(seriesName)) {
                const values = Object.values(data[seriesName])
                let processedValues = values.map(val => val || 0)
                if (convertToPercentage) {
                    processedValues = processedValues.map(val => val)
                }
                const baseColor = colorPalette[colorIndex % colorPalette.length]
                const transparentColor = this.hexToRgba(baseColor, fillOpacity)
                const hoverTemplate = this.getAreaChartHoverTemplate(seriesName, convertToPercentage)
                
                const trace = {
                    x: dates,
                    y: processedValues,
                    type: 'scatter',
                    fill: fillType,
                    fillcolor: transparentColor,
                    line: { 
                        color: baseColor,
                        width: lineWidth
                    },
                    name: seriesName,
                    hovertemplate: hoverTemplate,
                    hoverinfo: 'x+y+name',
                    hoveron: 'points'
                }

                if (stackGroup) {
                    trace.stackgroup = stackGroup
                }

                traces.push(trace)
                
                colorIndex++
            }
        })
        return traces
    }

    getAreaChartHoverTemplate(seriesName, convertToPercentage) {
        return `Credit Rating=${seriesName}<br>` +
               `Date=%{x}<br>` +
               `Percentage=${convertToPercentage ? '%{y:.0f}' : '%{y:.2f}'}<br>` +
               `<extra></extra>`
    }

    generateTimeSeriesPieChartTraces(data,xField,yField) {
        const labels = []
        const values = []
        if (data && Object.keys(data).length <= 0) {
            return []
        }
        Object.keys(data).forEach(sectorName => {
            if (sectorName !== 'Date') {
                const sectorValues = Object.values(data[sectorName])
                const lastIndex = sectorValues.length - 1
                const latestValue = sectorValues[lastIndex] || 0 
                
                labels.push(sectorName)
                const percentage = latestValue < 1 ? latestValue * 100 : latestValue
                values.push(percentage)
            }
        })
        
        let finalLabels = [...labels]
        let finalValues = [...values]
        
        const combined = labels.map((label, index) => ({
            label: label,
            value: values[index]
        }))
        
        const nonZeroItems = combined.filter(item => item.value > 0)
        const zeroItems = combined.filter(item => item.value === 0)
        
        if (zeroItems.length > 6) {
            finalLabels = nonZeroItems.map(item => item.label).concat(['Other'])
            finalValues = nonZeroItems.map(item => item.value).concat([0])
        }
        
        const trace = {
            labels: finalLabels,
            values: finalValues,
            type: 'pie',
            textinfo: 'percent',
            textposition: 'auto',
            automargin: true,
            marker: {
                colors: this.extendedColorPalette
            },
            hovertemplate: xField + ': %{label}<br>' +
                          yField + ': %{percent}%<br>' +
                          '<extra></extra>',
            text: finalLabels.map((label, index) => 
                label === 'Other' ? '0%' : ''
            ),
            texttemplate: finalLabels.map((label, index) => 
                label === 'Other' ? '0%' : '%{percent}'
            )
        }
        
        return [trace]
    }

    async updateCharts(key, mainId, chartTarget, title, xTitle, yTitle, chartType="pie",xField="", yField="") {
        let traces = null
        let layout = this.getPieChartLayout()
        try {
            this.showLoading(`${mainId}_${chartTarget}`)
            const data = await this.getChartDataByFilterData(mainId,key)
            if (!this[`${mainId}_${chartTarget}Target`] || !data) return
            switch (chartType) {
                case "pie":
                    traces = this.generatePieChartTraces(data, xField, yField)
                    layout = this.getPieChartLayout()
                    break
                case "bar":
                    traces = this.generateBarChartTraces(data, xField, yField)
                    layout = this.getBarChartLayout(xTitle, yTitle, title)
                    break
                case "line":
                    traces = this.generateLineChartTraces(data)
                    layout = this.getChartLayout(xTitle, yTitle, title)
                    break
                case "line_scatter":
                    traces = this.generateLineScatterChartTraces(data, xField, yField)
                    layout = this.getChartLayout(xTitle, yTitle, title)
                    break
                case "time_series_pie":
                    traces = this.generateTimeSeriesPieChartTraces(data, xField, yField)
                    layout = this.getPieChartLayout()
                    break
                    case "multi_area":
                    traces = this.generateMultiAreaChartTraces(data, {
                                convertToPercentage: false,
                                fillType: 'tonexty',
                                stackGroup: 'one',
                            })
                    layout = this.getMultiAreaChartLayout(xTitle, yTitle,title)
                    break
                case "multi_line_scatter":
                    const processedData = {
                        ...data,
                        'Credit Spreads': data['Credit Spreads'] ? Object.fromEntries(
                            Object.entries(data['Credit Spreads']).map(([key, value]) => [key, value * 100])
                        ) : data['Credit Spreads']
                    };
                    const creditSpreadDurationData = this.meltData(processedData, 'Date', ['Credit Spreads', 'Credit Duration'], 'Credit Type', 'Credit Value')
                    traces = this.generateMultiLineScatterChartTraces(creditSpreadDurationData, xField, yField, "Credit Type")
                    layout = this.getChartLayout(xTitle, yTitle, title)
                    break
            }

            if (traces && traces.length > 0 && layout) {
                this.drawChart(this[`${mainId}_${chartTarget}Target`], traces, layout)
            }
            else {
                this.drawChart(this[`${mainId}_${chartTarget}Target`], [], layout)
            }
        } catch (error) {
            this.drawChart(this[`${mainId}_${chartTarget}Target`], [], layout)
        }
        finally {
            this.hideLoading(`${mainId}_${chartTarget}`)
        }
    
    }

    async updateTable(key, mainId, tableTarget, tableGrid, columns){
        try{
            this.showLoading(`${mainId}_${tableTarget}`)
            const data = await this.getChartDataByFilterData(mainId, key)
            let formattedData = []
            if (data && this[`${mainId}_${tableTarget}Target`]) {
                if (tableTarget === "performanceTable"){
                    formattedData = this.convertPerformanceDataForSlickGrid(data)
                } else {
                    formattedData = this.convertDataForSlickGrid(data)
                }
            tableGrid = await this.createSlickGrid(this[`${mainId}_${tableTarget}Target`], formattedData, columns)
            } 
            else {
                tableGrid = await this.createSlickGrid(this[`${mainId}_${tableTarget}Target`], [], columns)
            }
        } catch (error) {
            console.error('Error updating table:', error)
        }
        finally {
            this.hideLoading(`${mainId}_${tableTarget}`)
        }
    }
    
    getPerformanceColumns() {
        return [
          { id: 'fund', name: 'Fund', field: 'Fund', minWidth: 250 },
          { id: '1m', name: '1m', field: '1m', minWidth: 120 },
          { id: '3m', name: '3m', field: '3m', minWidth: 120 },
          { id: '6m', name: '6m', field: '6m', minWidth: 120 },
          { id: '1y', name: '1y', field: '1y', minWidth: 120 },
          { id: '3y_pa', name: '3y p.a.', field: '3y p.a.', minWidth: 120 },
          { id: 'inception', name: 'Inception (cum)', field: 'Inception (cum)', minWidth: 250 },
        ]
    }

    getHoldingsColumns() {
        return [
          { id: 'isin',    name: 'ISIN',    field: 'ISIN',    minWidth: 140 },
          { id: 'security_name',    name: 'Security Name',    field: 'Security Name',    minWidth: 220 },
          { id: 'units',  name: 'Units',  field: 'Units',  minWidth: 120 },
          { id: 'issuer',  name: 'Portfolio Weight',  field: 'Portfolio Weight',  minWidth: 180 },
          { id: 'ba_rating',name: 'BA Rating',field: 'BA Rating',minWidth: 90  },
          { id: 'price',   name: 'Expected Yield', field: 'Expected Yield', minWidth: 150 },
          { id: 'rating',  name: 'Years to Maturity',  field: 'Years to Maturity',  minWidth: 100 },
          { id: 'capital_price',   name: 'Capital Price',   field: 'Capital Price',   minWidth: 100 },
          { id: 'running_yield',  name: 'Running Yield', field: 'Running Yield', minWidth: 180 },
          { id: 'mod_duration',  name: 'Mod Duration', field: 'Mod Duration', minWidth: 180 },
          { id: 'credit_duration',  name: 'Credit Duration', field: 'Credit Duration', minWidth: 180 },
          { id: 'credit_spread',  name: 'Credit Spread', field: 'Credit Spread', minWidth: 180 },
          { id: 'seniority',  name: 'Seniority', field: 'Seniority', minWidth: 220 },
          { id: 'payment_type',  name: 'Payment Type', field: 'Payment Type', minWidth: 180 },
          { id: 'security_rating',  name: 'S&P/Moody\'s/Fitch Security Rating', field: 'S&P/Moody\'s/Fitch Security Rating', minWidth: 220 },
        ]
    }

    getTransactionHistoryColumns() {
        return [
          { id: 'isin',    name: 'ISIN',    field: 'ISIN',    minWidth: 140 },
          { id: 'security',    name: 'Security',    field: 'Security',    minWidth: 220 },
          { id: 'trade_date',  name: 'Trade Date',  field: 'Trade Date',  minWidth: 120 },
          { id: 'settlement_date',  name: 'Settlement Date',  field: 'Settlement Date',  minWidth: 120 },
          { id: 'type',name: 'Type',field: 'Type',minWidth: 90  },
          { id: 'units',   name: 'Units', field: 'Units', minWidth: 90 },
          { id: 'gross_price',  name: 'Gross Price',  field: 'Gross Price',  minWidth: 150 },
        ]
    }
    
    createSlickGrid(target, data, columns, options = {}) {
           
        const defaultOptions = { 
          rowHeight: 32, 
          forceFitColumns: true,
          enableColumnReorder: true,
          enableTextSelectionOnCells: true,
          enableCellNavigation: true,
          autoEdit: false
        }
        const grid = new SlickGrid(target, data, columns, { ...defaultOptions, ...options })
        
        if (!document.getElementById('slick-grid-styles')) {
          const style = document.createElement('style')
          style.id = 'slick-grid-styles'
          style.textContent = `
            .slick-cell {
                display: flex !important;
                align-items: center !important;
                vertical-align: middle !important;
                line-height: 1 !important;
            }
            .slick-cell input {
                margin: 0 !important;
                vertical-align: middle !important;
            }
            `
          document.head.appendChild(style)
        }
        
        setTimeout(() => { grid.resizeCanvas() }, 100)
        return grid
    }
}
